/**
 * @fileoverview Reusable empty state component for displaying when no data is available.
 * Provides consistent styling and layout for icon, title, and subtitle across the application.
 */

import type { ReactNode } from 'react';
import { Box, Typography, type SxProps, type Theme } from '@mui/material';

export interface EmptyStateProps {
  /**
   * Icon to display at the top of the empty state
   */
  icon: ReactNode;
  /**
   * Primary message to display
   */
  title: string;
  /**
   * Optional secondary message for additional context
   */
  subtitle?: string;
  /**
   * Size of the icon (fontSize value)
   * @default 48
   */
  iconSize?: number;
  /**
   * Additional sx props for the container
   */
  sx?: SxProps<Theme>;
}

/**
 * Displays a centered empty state with an icon, title, and optional subtitle.
 * Used when lists, drawers, or grids have no content to display.
 */
export function EmptyState({
  icon,
  title,
  subtitle,
  iconSize = 48,
  sx,
}: EmptyStateProps) {
  return (
    <Box sx={{ textAlign: 'center', mt: 6, ...sx }}>
      <Box sx={{ fontSize: iconSize, color: 'text.disabled', mb: 2 }}>
        {icon}
      </Box>
      <Typography
        variant="body1"
        sx={{
          color: "text.secondary",
          mb: 1
        }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" sx={{
          color: "text.disabled"
        }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}
